/**
 * Funding outlook — `GET /closing/funding-outlook?start&end&pipeline`
 * (closing-readiness.md §5): expected fundings by DATE across both pipelines,
 * for liquidity prep. Closed loans are excluded (no liquidity need). One row
 * per day in an AG Grid, expandable to the day's cards.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, CircularProgress, MenuItem, TextField } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
import { addDays, format } from "date-fns";
import { useNavigate } from "react-router-dom";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import { Pill, ReadinessBadge } from "../../components/closingReadiness/ReadinessBits";
import {
  cardDisplayName,
  eventNoun,
  formatCompactDollar,
  pipelineStatusLabel,
} from "../../components/closingReadiness/closingReadinessUtils";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { READINESS_PIPELINES, closingReadinessApi } from "../../api/closingReadinessApi";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { slate, status, steel } from "../../theme/colors";
import { C, CenterCell, MONO, StatCard } from "./fundingsUi";

// AgGridTableWrapper only registers the community bundle.
ModuleRegistry.registerModules([MasterDetailModule]);

const WINDOWS = [
  { days: 14, label: "Next 2 weeks" },
  { days: 30, label: "Next 30 days" },
  { days: 60, label: "Next 60 days" },
  { days: 90, label: "Next 90 days" },
];
const PIPELINES = [
  { value: READINESS_PIPELINES.ALL, label: "Closings + settlements" },
  { value: READINESS_PIPELINES.PRECLOSE, label: "Closings only" },
  { value: READINESS_PIPELINES.ACQUISITION, label: "Trade settlements only" },
];

const DayDetail = ({ data, navigate }) => (
  <Box sx={{ px: 2, py: 1.25, background: C.bg }}>
    <Flx column g={0.5}>
      {(data?.items || []).map((card) => {
        const href = `${card.pipeline === "acquisition" ? "/acquisition" : "/loan"}/${card.dealId}/closing`;
        return (
          <Flx
            ac
            g={1.25}
            key={card.dealId}
            onClick={() => navigate(href)}
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: "8px",
              background: C.surface,
              border: `1px solid ${C.line}`,
              cursor: "pointer",
              "&:hover": { borderColor: C.faint },
            }}
          >
            <Txt sx={{ fontFamily: MONO, fontSize: 11.5, color: C.faint, width: 90, flexShrink: 0 }}>
              #{card.loanNumber ?? "—"}
            </Txt>
            <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {cardDisplayName(card)}
            </Txt>
            <Pill label={eventNoun(card)} c={card.pipeline === "acquisition" ? steel.main : slate[600]} bg={card.pipeline === "acquisition" ? steel.tint : slate[100]} size="xs" />
            <Txt sx={{ fontSize: 11.5, color: C.sub, width: 170, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {pipelineStatusLabel(card)}
            </Txt>
            <ReadinessBadge level={card.readinessLevel} signal={card.fundingSignal} size="xs" />
            <Txt sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: C.ink, width: 100, textAlign: "right", flexShrink: 0 }}>
              {formatDollar(card.loanAmount, "—")}
            </Txt>
          </Flx>
        );
      })}
    </Flx>
  </Box>
);

const FundingOutlookView = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [pipeline, setPipeline] = useState(READINESS_PIPELINES.ALL);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const start = new Date();
      setData(
        await closingReadinessApi.getFundingOutlook({
          start: format(start, "yyyy-MM-dd"),
          end: format(addDays(start, days), "yyyy-MM-dd"),
          pipeline,
        }),
      );
    } catch (err) {
      console.error("funding outlook error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, pipeline]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => (Array.isArray(data?.days) ? data.days : []), [data]);
  const totals = data?.totals || {};

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Date",
        colId: "date",
        width: 170,
        cellRenderer: "agGroupCellRenderer",
        valueGetter: (p) => p.data?.date,
        valueFormatter: (p) => formatDate(p.value, "—", "EEE, MMM d"),
      },
      { headerName: "Loans", colId: "count", width: 90, valueGetter: (p) => p.data?.count ?? 0 },
      {
        headerName: "Amount",
        colId: "amount",
        width: 130,
        type: "rightAligned",
        valueGetter: (p) => p.data?.amount,
        valueFormatter: (p) => formatDollar(p.value, "—"),
        cellStyle: { fontFamily: MONO },
      },
      {
        headerName: "Ready",
        colId: "ready",
        width: 150,
        valueGetter: (p) => p.data?.readyCount ?? 0,
        cellRenderer: (p) => (
          <CenterCell>
            <Pill
              label={`${p.value} · ${formatCompactDollar(p.data?.readyAmount) || "$0"}`}
              c={p.value ? status.success.fg : slate[500]}
              bg={p.value ? status.success.bg : slate[100]}
              size="xs"
            />
          </CenterCell>
        ),
      },
      {
        headerName: "Closings / trades",
        colId: "split",
        width: 150,
        valueGetter: (p) => `${p.data?.closings ?? 0} / ${p.data?.trades ?? 0}`,
        cellStyle: { fontFamily: MONO },
      },
      {
        headerName: "Wire",
        colId: "wire",
        width: 120,
        valueGetter: (p) => !!p.data?.wireBlocked,
        cellRenderer: (p) => (
          <CenterCell>
            {p.value ? <Pill label="Wire-blocked" c={status.error.fg} bg={status.error.bg} size="xs" /> : <Txt sx={{ fontSize: 11.5, color: C.faint }}>—</Txt>}
          </CenterCell>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Flx ac wrap g={1.5} sx={{ mb: 2 }}>
        <TextField select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 150, background: C.surface, ".MuiInputBase-root": { fontSize: 13 } }}>
          {WINDOWS.map((w) => (
            <MenuItem key={w.days} value={w.days} sx={{ fontSize: 13 }}>
              {w.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" value={pipeline} onChange={(e) => setPipeline(e.target.value)} sx={{ minWidth: 200, background: C.surface, ".MuiInputBase-root": { fontSize: 13 } }}>
          {PIPELINES.map((p) => (
            <MenuItem key={p.value} value={p.value} sx={{ fontSize: 13 }}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        {loading && <CircularProgress size={16} sx={{ color: C.faint }} />}
      </Flx>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
        <StatCard label="Expected fundings" value={totals.count ?? rows.reduce((s, d) => s + (d.count || 0), 0)} sub={`${rows.length} funding day${rows.length === 1 ? "" : "s"}`} />
        <StatCard label="Expected amount" value={formatCompactDollar(totals.amount ?? rows.reduce((s, d) => s + (d.amount || 0), 0)) || "$0"} sub="closed loans excluded" />
        <StatCard label="Ready to fund" value={formatCompactDollar(totals.readyAmount ?? rows.reduce((s, d) => s + (d.readyAmount || 0), 0)) || "$0"} sub={`${totals.readyCount ?? rows.reduce((s, d) => s + (d.readyCount || 0), 0)} loans`} />
        <StatCard label="Wire-blocked days" value={rows.filter((d) => d.wireBlocked).length} alert={rows.some((d) => d.wireBlocked)} sub="bank holidays in the window" />
      </Box>

      <AgGridTableWrapper height="auto">
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={{ resizable: true, sortable: true, suppressHeaderMenuButton: true, cellStyle: { display: "flex", alignItems: "center" } }}
          getRowId={(p) => p.data?.date}
          domLayout="autoHeight"
          rowHeight={46}
          headerHeight={38}
          suppressCellFocus
          masterDetail
          detailCellRenderer={(p) => <DayDetail data={p.data} navigate={navigate} />}
          detailRowAutoHeight
          rowStyle={{ cursor: "pointer" }}
          onRowClicked={(e) => e.node.setExpanded(!e.node.expanded)}
          overlayNoRowsTemplate={loading ? "<span></span>" : "<span style='font-size:12.5px;color:#94a3b8'>No expected fundings in this window</span>"}
        />
      </AgGridTableWrapper>
    </Box>
  );
};

export default FundingOutlookView;
